import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { 
  MessageSquare, 
  Send, 
  Trash2, 
  User, 
  Lock,
  Clock,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { value: "general", label: "Général" },
  { value: "effectifs", label: "Effectifs" },
  { value: "emploi", label: "Emploi" },
  { value: "absenteisme", label: "Absentéisme" },
  { value: "remuneration", label: "Rémunération" },
  { value: "sante_securite", label: "Santé & Sécurité" },
];

export default function BilanComments({ bilanId, bilanStatus }) {
  const { user, isAdmin, isCabinet } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, commentId: null });
  
  const [newComment, setNewComment] = useState({
    content: "",
    section: "",
    is_internal: false
  });
  
  const canAddComment = isAdmin || 
    user?.role === "auditeur" || 
    (isCabinet && bilanStatus === "brouillon");

  useEffect(() => {
    fetchComments();
  }, [bilanId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/bilans/${bilanId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error(/* PROD */ "Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newComment.content.trim()) {
      toast.error("Veuillez saisir un commentaire");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/bilans/${bilanId}/comments`, {
        content: newComment.content.trim(),
        section: newComment.section || null,
        is_internal: newComment.is_internal
      });
      toast.success("Commentaire ajouté");
      setNewComment({ content: "", section: "", is_internal: false });
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout du commentaire");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.commentId) return;

    try {
      await axios.delete(`${API_URL}/bilans/${bilanId}/comments/${deleteDialog.commentId}`);
      toast.success("Commentaire supprimé");
      setDeleteDialog({ open: false, commentId: null });
      fetchComments();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { label: "Super Admin", variant: "default" },
      admin_igat: { label: "IGAT", variant: "secondary" },
      admin_igt: { label: "Admin", variant: "secondary" },
      auditeur: { label: "Contrôleur", variant: "outline" },
      cabinet: { label: "Cabinet", variant: "outline" }
    };
    return config[role] || { label: role, variant: "outline" };
  };

  const canDeleteComment = (comment) => {
    if (isAdmin) return true;
    return comment.user_id === user?.id;
  };

  return (
    <Card className="mt-6" data-testid="bilan-comments">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="w-5 h-5 text-[#1B4D3E]" />
            Commentaires & Observations
            {comments.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {comments.length}
              </Badge>
            )}
          </CardTitle>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-4">
          {/* Add comment form */}
          {canAddComment && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  Ajouter une observation
                </span>
              </div>
              
              <Textarea
                placeholder="Écrivez votre commentaire ou observation ici..."
                value={newComment.content}
                onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                rows={3}
                data-testid="comment-input"
              />
              
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Select
                    value={newComment.section}
                    onValueChange={(value) => setNewComment({ ...newComment, section: value })}
                  >
                    <SelectTrigger className="h-9" data-testid="comment-section-select">
                      <SelectValue placeholder="Section (optionnel)" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTIONS.map(section => (
                        <SelectItem key={section.value} value={section.value}>
                          {section.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {(isAdmin || user?.role === "auditeur") && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="internal"
                      checked={newComment.is_internal}
                      onCheckedChange={(checked) => setNewComment({ ...newComment, is_internal: checked })}
                    />
                    <Label htmlFor="internal" className="text-sm text-gray-600 cursor-pointer flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Commentaire interne
                    </Label>
                  </div>
                )}
                
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !newComment.content.trim()}
                  className="bg-[#1B4D3E] hover:bg-[#163d32]"
                  data-testid="submit-comment-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? "Envoi..." : "Envoyer"}
                </Button>
              </div>
            </div>
          )}

          {/* Comments list */}
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B4D3E] mx-auto"></div>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Aucun commentaire pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {comments.map((comment) => (
                <div 
                  key={comment.id} 
                  className={`p-4 rounded-lg border ${
                    comment.is_internal 
                      ? 'bg-amber-50 border-amber-200' 
                      : 'bg-white border-gray-200'
                  }`}
                  data-testid={`comment-${comment.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-medium text-gray-900">
                          {comment.user_name}
                        </span>
                        <Badge {...getRoleBadge(comment.user_role)}>
                          {getRoleBadge(comment.user_role).label}
                        </Badge>
                        {comment.section && (
                          <Badge variant="outline" className="text-xs">
                            {SECTIONS.find(s => s.value === comment.section)?.label || comment.section}
                          </Badge>
                        )}
                        {comment.is_internal && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800 gap-1">
                            <Lock className="w-3 h-3" />
                            Interne
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {comment.content}
                      </p>
                      
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {new Date(comment.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    {canDeleteComment(comment) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-gray-400 hover:text-red-600 h-8 w-8"
                        onClick={() => setDeleteDialog({ open: true, commentId: comment.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, commentId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce commentaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
